"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { useCart } from "@/hooks/useCart";
import { useCustomer } from "@/hooks/useCustomer";
import { Locales } from "@/lib/i18n";
import { toast } from "@/components/atoms/toast";
import { __, getPermalink } from "@/lib/i18n/functions";
import { isThankYouRoute } from "@/components/templates/Checkout";

type CheckoutRouteGuardProps = {
  children: React.ReactNode;
  locale: Locales;
  route: string;
};

export const CheckoutRouteGuard = ({ children, locale, route }: CheckoutRouteGuardProps) => {
  const { cart } = useCart();
  const { customer } = useCustomer();
  const router = useRouter();

  useEffect(() => {
    // Skip guard logic for thank you page
    if (isThankYouRoute(locale, route)) {
      return;
    }

    // Wait for both cart and customer to be loaded
    if (cart === undefined || customer === undefined) {
      return; // Still loading
    }

    const hasItems = cart?.items && cart.items.length > 0;
    const isLoggedIn = customer !== null;

    if (!isLoggedIn && !hasItems) {
      toast.error({ title: __("Please add products to cart and login to place an order", locale) });
      router.push(getPermalink(locale, "cart"));
      return;
    } // No customer and no cart items - redirect to cart

    if (!isLoggedIn) {
      router.push(getPermalink(locale, "login"));
      toast.error({
        title: __("Please log in to place an order", locale),
      });
      return;
    } // No customer but has cart items - redirect to login

    if (!hasItems) {
      toast.error({
        title: __("No products in cart added", locale),
        description: __("Please add products to cart before proceeding.", locale),
      });
      router.push(getPermalink(locale, "cart"));
      return;
    } // Logged in but no cart items - redirect to cart

    // All conditions met, user can proceed with checkout
  }, [cart, customer, router, locale, route]);

  // Skip loading state for thank you page
  if (isThankYouRoute(locale, route)) {
    return <>{children}</>;
  }

  // Loading state while checking
  if (cart === undefined || customer === undefined) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="border-dark-primary h-8 w-8 animate-spin rounded-full border-b-2"></div>
      </div>
    );
  }

  // Check conditions again for rendering
  const hasItems = cart?.items && cart.items.length > 0;
  const isLoggedIn = customer !== null;

  if (!isLoggedIn || !hasItems) {
    // Return loading state while redirect is happening
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="border-dark-primary h-8 w-8 animate-spin rounded-full border-b-2"></div>
      </div>
    );
  }

  return <>{children}</>;
};
